/** @jsxImportSource @emotion/react */
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, useTheme } from '@mui/material'
import { DialogFooter, DialogHeader, DialogTitle } from '@app/components/ui/Dialog/Dialog'
import { toast } from '@app/components/ui/Toast/toast'
import { addLayersDialogAtom } from '@app/state/layoutAtom'
import { useSetAtom } from 'jotai'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { WorkspaceFormData, WorkspaceSchema } from './utils/Workspaces.utils'
import { contentAreaStyles } from './WorkspacesDialog.styles'
import WorkspacesForm from './WorkspacesForm'
import { WorkspaceNavbar } from './WorkspaceNavbar/WorkspaceNavbar'
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { useMapLayers } from '@app/hooks/useMapLayers'
import { useWorkspaceBuilder } from './hooks/useWorkspaceBuilder'
import { useWorkspaceLoader } from './hooks/useWorkspaceLoader'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'

const WorkspacesCreate = () => {
  const navigate = useNavigate()
  const setAddLayersDialogAtom = useSetAtom(addLayersDialogAtom)
  const theme = useTheme()
  const { layers, isMapEmpty } = useMapLayers()
  const { loadWorkspace } = useWorkspaceLoader()
  const workspaceBuilder = useWorkspaceBuilder()
  const { regionMasks } = useMap()
  const liveDrawAreas = useSelector((state: RootState) => state.riskMap.drawAreas)

  const form = useForm<WorkspaceFormData>({
    resolver: zodResolver(WorkspaceSchema),
    defaultValues: {
      name: '',
      description: '',
      visibility: 'private',
    },
  })

  const onSubmit = async (data: WorkspaceFormData & { visibility: 'private' | 'organisation' }) => {
    try {
      const result = await workspaceBuilder.createWorkspace(data)
      if (!result) return
      loadWorkspace(result)

      // Succeeded
      logDataDogAction(DATADOG_ACTIONS.WORKSPACE_CREATED)

      toast({
        title: (
          <span>
            <b>{data.name}</b> has been created
          </span>
        ),
        description:
          data.visibility === 'organisation'
            ? 'Anyone in your organisation can use it.'
            : undefined,
        duration: 10000,
        type: 'success',
        position: 'top-right',
      })
      form.reset()
      navigate('../..')
    } catch (error) {
      if (error instanceof Error) {
        if (error.cause === 'duplicate_name' && error.message) {
          form.setError('name', { message: error.message })
          return
        }
        form.setError('root', {
          message: error.message,
        })
      }
      form.setError('root', {
        message: 'An unexpected error occurred',
      })
    }
  }

  const onCancel = () => {
    navigate('../..')
  }

  let content = null
  if (isMapEmpty) {
    content = (
      <Box css={contentAreaStyles(theme)}>
        <EmptyData
          title="No Layers to Save"
          description="Add layers to the map to create and save a Workspace."
          actionLabel="Add layers"
          onAction={() => {
            navigate('../..')
            setAddLayersDialogAtom(true)
          }}
        />
      </Box>
    )
  } else {
    content = (
      <Box css={contentAreaStyles(theme)}>
        <DialogHeader>
          <DialogTitle>Create Workspace</DialogTitle>
        </DialogHeader>
        <WorkspacesForm
          form={form}
          layers={layers}
          regions={regionMasks?.map((r) => r.region) ?? []}
          drawAreas={liveDrawAreas}
          previewConfig={{
            titleOverride: 'Active layers',
            descriptionOverride: null,
          }}
        />
        <DialogFooter>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isMapEmpty || form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </Box>
    )
  }

  return (
    <>
      <WorkspaceNavbar currentTab="create" />
      {content}
    </>
  )
}

export default WorkspacesCreate
